import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { Button } from 'src/component/ds/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';

export interface TriagedMetricProps {
  icon: React.ElementType;
  label: string;
  linkLabel: string;
  onLinkClick: () => void;
  segments?: number[];
  labelHint?: string;
  testId: string;
}

/**
 * One sub-metric in the Triaged Alarms card: an icon and label, the segmented
 * bar beneath them, and a footer chip that drills through.
 *
 * @param {TriagedMetricProps} props see TriagedMetricProps
 * @returns {JSX.Element} the sub-metric block
 */
export default function TriagedMetric ({
  icon: Icon,
  label,
  linkLabel,
  onLinkClick,
  segments = [],
  labelHint,
  testId
}: TriagedMetricProps) {

  const isEmpty = !segments.length;
  const bars = isEmpty ? [1] : segments;

  return (
    <div
      className={cn(
        'ds-flex ds-flex-col ds-gap-4',
        'ds-narrow:ds-grid ds-narrow:ds-grid-rows-subgrid ds-narrow:ds-row-span-3 ds-narrow:ds-gap-0'
      )}
      data-testid={testId}
    >
      <div className='ds-flex ds-flex-col ds-gap-3 ds-border-l ds-border-border ds-narrow:ds-row-span-2 ds-narrow:ds-grid ds-narrow:ds-grid-rows-subgrid ds-narrow:ds-gap-0'>
        <span className='ds-flex ds-min-w-0 ds-items-start ds-gap-2 ds-pl-2'>
          <Icon aria-hidden='true' sx={{ fontSize: 24 }} className='ds-shrink-0 ds-text-foreground' />
          <span className='ds-text-ds-base ds-font-medium ds-text-foreground'>{label}</span>

          {labelHint && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  aria-label={labelHint}
                  className={cn(
                    'ds-flex ds-shrink-0 ds-cursor-default ds-items-center ds-bg-transparent ds-p-0',
                    'ds-text-muted-foreground hover:ds-text-foreground',
                    'focus-visible:ds-outline-none focus-visible:ds-shadow-ds-focus'
                  )}
                  data-testid={`${testId}-info`}
                >
                  <InfoOutlined aria-hidden='true' sx={{ fontSize: 16 }} />
                </button>
              </TooltipTrigger>
              <TooltipContent>{labelHint}</TooltipContent>
            </Tooltip>
          )}
        </span>

        <span
          className='ds-flex ds-w-full ds-items-stretch ds-gap-2 ds-narrow:ds-mt-3'
          aria-hidden='true'
          data-testid={`${testId}-bar`}
        >
          {bars.map((weight, idx) => (
            <span
              key={idx}
              style={{ flexGrow: weight }}
              className={cn(
                'ds-block ds-h-[14px] ds-basis-0 ds-rounded-[2px]',
                isEmpty ? 'ds-bg-transparent' : 'ds-bg-secondary-foreground'
              )}
            />
          ))}
        </span>
      </div>

      <Button
        variant='outline'
        size='xs'
        className='ds-self-start ds-shadow-ds-xs ds-narrow:ds-mt-4'
        onClick={onLinkClick}
        data-testid={`${testId}-link`}
      >
        <span>{linkLabel}</span>
        <ChevronRightIcon fontSize='inherit' aria-hidden='true' />
      </Button>
    </div>
  );
}
